// src/components/SplashScreen.js
import { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import logo from "../assets/logo.png";

/* ============================================================
   KEYFRAMES
============================================================ */

const fadeOut = keyframes`
  0%   { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(1.03); pointer-events: none; }
`;

const gridDrift = keyframes`
  from { transform: translateY(0); }
  to   { transform: translateY(60px); }
`;

const scanLine = keyframes`
  0%   { transform: translateY(-100%); opacity: 0; }
  10%  { opacity: 1; }
  90%  { opacity: 1; }
  100% { transform: translateY(100vh); opacity: 0; }
`;

const logoEntrance = keyframes`
  0%   { opacity: 0; transform: scale(0.35) rotate(-12deg); }
  60%  { opacity: 1; transform: scale(1.1) rotate(3deg); }
  80%  { transform: scale(0.95) rotate(-1deg); }
  100% { opacity: 1; transform: scale(1) rotate(0deg); }
`;

const pulseGlow = keyframes`
  0%   { filter: drop-shadow(0 0 10px rgba(41,171,226,0.65)) drop-shadow(0 0 28px rgba(41,171,226,0.25)); }
  50%  { filter: drop-shadow(0 0 22px rgba(247,148,29,0.95)) drop-shadow(0 0 55px rgba(247,148,29,0.35)); }
  100% { filter: drop-shadow(0 0 10px rgba(41,171,226,0.65)) drop-shadow(0 0 28px rgba(41,171,226,0.25)); }
`;

const rippleExpand = keyframes`
  0%   { transform: translate(-50%, -50%) scale(0.5); opacity: 0.55; }
  100% { transform: translate(-50%, -50%) scale(2.6); opacity: 0; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shinePass = keyframes`
  0%   { transform: translateX(-250%) skewX(-20deg); }
  100% { transform: translateX(500%) skewX(-20deg); }
`;

const barGlow = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(41,171,226,0.55); }
  50%       { box-shadow: 0 0 22px rgba(247,148,29,0.80), 0 0 44px rgba(247,148,29,0.25); }
`;

const textGlow = keyframes`
  0%, 100% { text-shadow: 0 0 14px rgba(41,171,226,0.5), 0 0 40px rgba(41,171,226,0.2); }
  50%       { text-shadow: 0 0 22px rgba(247,148,29,0.7), 0 0 70px rgba(247,148,29,0.25); }
`;

const dotBlink = keyframes`
  0%, 80%, 100% { opacity: 0.15; transform: scale(0.7); }
  40%           { opacity: 1;    transform: scale(1); }
`;

const statusFade = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ============================================================
   LAYOUT WRAPPER
============================================================ */

const SplashWrapper = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  /* Deep navy radial gradient */
  background:
    radial-gradient(ellipse at 25% 15%, rgba(15,74,110,0.9) 0%, transparent 55%),
    radial-gradient(ellipse at 75% 85%, rgba(20,40,70,0.7) 0%, transparent 50%),
    linear-gradient(160deg, #071d2e 0%, #030e1a 60%, #04111f 100%);

  overflow: hidden;
  z-index: 9999;

  animation: ${fadeOut} 0.85s ease forwards;
  animation-delay: 3.7s;
`;

/* ============================================================
   BACKGROUND LAYERS
============================================================ */

const GridBg = styled.div`
  position: absolute;
  inset: -60px 0 0 0;
  background-image:
    linear-gradient(rgba(41,171,226,0.065) 1px, transparent 1px),
    linear-gradient(90deg, rgba(41,171,226,0.065) 1px, transparent 1px);
  background-size: 60px 60px;
  animation: ${gridDrift} 5s linear infinite;
  mask-image: radial-gradient(ellipse 80% 70% at center, black 30%, transparent 85%);
`;

const ScanLineDiv = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(41,171,226,0.25) 20%,
    rgba(247,148,29,0.18) 50%,
    rgba(41,171,226,0.25) 80%,
    transparent 100%
  );
  animation: ${scanLine} 5s linear infinite;
  animation-delay: 0.8s;
`;

/* ============================================================
   RIPPLE RINGS (centered absolutely behind logo)
============================================================ */

const RippleRing = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 170px;
  height: 170px;
  border-radius: 50%;
  border: 1.5px solid rgba(41,171,226,0.28);
  animation: ${rippleExpand} 3.2s ease-out infinite;
  animation-delay: ${({ $delay }) => $delay ?? "0s"};
`;

/* ============================================================
   LOGO
============================================================ */

const LogoContainer = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  margin-bottom: 1.8rem;
`;

const LogoImg = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 22px;
  position: relative;
  z-index: 2;
  opacity: 0;
  animation:
    ${logoEntrance} 0.85s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.2s forwards,
    ${pulseGlow} 3s 1.1s ease-in-out infinite;
`;

const ShineOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 22px;
  overflow: hidden;
  z-index: 3;
  pointer-events: none;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 48px;
    height: 100%;
    background: rgba(255, 255, 255, 0.38);
    filter: blur(10px);
    animation: ${shinePass} 3s ease-in-out infinite;
    animation-delay: 1.4s;
  }
`;

/* ============================================================
   TEXT
============================================================ */

const LogoCenter = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const BrandName = styled.h1`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.9rem, 4vw, 2.6rem);
  font-weight: 800;
  letter-spacing: 0.08em;
  margin: 0 0 0.45rem;
  background: linear-gradient(135deg, #29ABE2 0%, #e0f4ff 45%, #F7941D 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  opacity: 0;
  animation:
    ${fadeInUp} 0.65s ease 0.8s forwards,
    ${textGlow} 3s 1.8s ease-in-out infinite;
`;

const Tagline = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.68rem, 1.5vw, 0.82rem);
  font-weight: 400;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(160, 215, 240, 0.72);
  margin: 0 0 2.6rem;
  opacity: 0;
  animation: ${fadeInUp} 0.65s ease 1.05s forwards;
`;

/* ============================================================
   PROGRESS
============================================================ */

const ProgressContainer = styled.div`
  width: min(260px, 72vw);
  opacity: 0;
  animation: ${fadeInUp} 0.55s ease 1.35s forwards;
`;

const ProgressTrack = styled.div`
  height: 4px;
  background: rgba(255, 255, 255, 0.09);
  border-radius: 100px;
  overflow: visible;
  margin-bottom: 0.85rem;
  position: relative;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: linear-gradient(90deg, #29ABE2 0%, #4ec9f0 40%, #F7941D 100%);
  border-radius: 100px;
  transition: width 0.22s ease;
  animation: ${barGlow} 2.2s ease-in-out infinite;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    right: -1px;
    top: 50%;
    transform: translateY(-50%);
    width: 10px;
    height: 10px;
    background: #fff;
    border-radius: 50%;
    box-shadow: 0 0 10px rgba(247,148,29,0.9), 0 0 22px rgba(247,148,29,0.4);
    opacity: ${({ $progress }) => ($progress > 0 && $progress < 100 ? 1 : 0)};
    transition: opacity 0.3s;
  }
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StatusText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 0.76rem;
  color: rgba(150, 205, 230, 0.68);
  margin: 0;
  letter-spacing: 0.04em;
  animation: ${statusFade} 0.35s ease forwards;
  key: ${({ $key }) => $key};
`;

const ProgressPct = styled.span`
  font-family: 'Poppins', monospace;
  font-size: 0.72rem;
  color: rgba(247, 148, 29, 0.65);
  letter-spacing: 0.04em;
`;

const Dots = styled.span`
  display: inline-flex;
  gap: 3px;
  margin-left: 4px;
  vertical-align: middle;

  span {
    display: inline-block;
    width: 3px;
    height: 3px;
    background: rgba(150, 205, 230, 0.7);
    border-radius: 50%;
    animation: ${dotBlink} 1.4s infinite;
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;

/* ============================================================
   FOOTER TAG
============================================================ */

const VersionTag = styled.span`
  position: absolute;
  bottom: 1.8rem;
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.15);
  letter-spacing: 0.18em;
  font-family: 'Courier New', monospace;
  text-transform: uppercase;
  opacity: 0;
  animation: ${fadeInUp} 0.5s ease 1.6s forwards;
`;

/* ============================================================
   COMPONENT
============================================================ */

const MESSAGES = [
  "Initializing AI models",
  "Loading market data",
  "Calibrating signals",
  "Trend Genie ready",
];

const SplashScreen = () => {
  const [progress, setProgress] = useState(0);
  const [msgIdx, setMsgIdx] = useState(0);

  useEffect(() => {
    let p = 0;
    const interval = setInterval(() => {
      p += Math.random() * 9 + 2.5;
      if (p >= 100) {
        p = 100;
        clearInterval(interval);
      }
      setProgress(p);
      setMsgIdx(Math.min(3, Math.floor(p / 25)));
    }, 140);
    return () => clearInterval(interval);
  }, []);

  const isDone = progress >= 100;

  return (
    <SplashWrapper>
      {/* Background layers */}
      <GridBg />
      <ScanLineDiv />

      {/* Ripple rings — centered on logo */}
      <RippleRing $delay="0s" />
      <RippleRing $delay="1.05s" />
      <RippleRing $delay="2.1s" />

      {/* Main content */}
      <LogoCenter>
        <LogoContainer>
          <LogoImg src={logo} alt="Trend Genie" />
          <ShineOverlay />
        </LogoContainer>

        <BrandName>TREND GENIE</BrandName>
        <Tagline>AI-Powered Investment Analysis</Tagline>

        <ProgressContainer>
          <ProgressTrack>
            <ProgressFill $progress={progress} />
          </ProgressTrack>
          <StatusRow>
            <StatusText $key={msgIdx}>
              {isDone ? "Welcome" : MESSAGES[msgIdx]}
              {!isDone && (
                <Dots>
                  <span />
                  <span />
                  <span />
                </Dots>
              )}
            </StatusText>
            <ProgressPct>{Math.round(progress)}%</ProgressPct>
          </StatusRow>
        </ProgressContainer>
      </LogoCenter>

      <VersionTag>Trend Genie &nbsp;·&nbsp; AI Trading Signals</VersionTag>
    </SplashWrapper>
  );
};

export default SplashScreen;
